import { useInfiniteQuery, useQuery } from '@tanstack/vue-query';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import http from '@/services/http';
import type { PlaylistTrackRow, TrackVotesContext } from '@/composables/api/usePlaylistSwitchingApi';

export interface LiveTrackUsage {
  source_type: 'bucket' | 'custom_playlist';
  source_id: number;
  source_name: string | null;
  /** "{account} / {location} / {zone} — {list}" composed by the backend's shared helper. */
  display_string: string;
  location_zone_id: number | null;
  location_zone_name: string | null;
  location_id: number | null;
  location_name: string | null;
  account_id: number | null;
  account_name: string | null;
  /** The zone this list's zone subscribes to, when it has one. */
  master_location_zone_id: number | null;
  master_location_zone_name: string | null;
  /** How many zones subscribe to this list's zone. Above zero, the zone is a master. */
  subscribers_count: number;
  /** Null when the list has not been counted yet — shown as a dash, not 0. */
  track_count: number | null;
  /** Custom lists only; 3 is disabled. */
  location_zone_custom_playlist_status_id: number | null;
  /** The bucket or its zone is archived. Should not happen; flagged in case the table is stale. */
  is_archived: boolean;
  /** The list's zone, or that zone's master, bans this track. */
  is_banned: boolean;
  /** Shaped for PlaylistContainerZoneLine: the zone carries the facts its icon needs. */
  location_zone: LiveTrackUsageZoneReference | null;
  location: { id: number; display_string: string | null } | null;
  master_location_zone: (LiveTrackUsageZoneReference & {
    location: { id: number; display_string: string | null } | null;
  }) | null;
}

export interface LiveTrackUsageZoneReference {
  id: number;
  display_string: string | null;
  master_location_zone_id: number | null;
  subscribers_count: number;
  /** Turns the zone's icon red when it is Pending Cancelation. */
  location_zone_status_id: number | null;
}

/** What "ban everywhere" would do, worked out by the server over the full set of zones. */
export interface TrackBanEverywherePlan {
  zones: number;
  zones_to_ban: number;
  zones_already_banned: number;
  zones_inheriting_ban: number;
  subscriber_zones_to_push: number;
  hand_built_lists_losing_track: number;
}

export interface LiveTrackUsagesResponse {
  data: LiveTrackUsage[];
  meta: {
    total: number;
    truncated: boolean;
    /** Null for people who cannot ban. */
    ban_everywhere: TrackBanEverywherePlan | null;
  };
}

/**
 * What a finished ban reports, as the async-operation broadcast carries it. A track can be on
 * 1,500 zones, too many to name one by one in a broadcast, so zones are counts and only the
 * first failures are named.
 */
export interface TrackBanEverywhereResult {
  summary: {
    zones: number;
    banned: number;
    failed: number;
    subscriber_zones_pushed: number;
    hand_built_list_rows_removed: number;
  };
  zone_status_counts: { banned: number; already_banned: number; inherits_master: number; failed: number };
  failed_zones: { location_zone_id: number; location_zone_name: string | null; message: string | null }[];
  players_not_notified_zone_count: number;
  players: {
    pushed: number;
    /** Players that take commands, queued because the ban reached too many to push inline. */
    queued: number;
    skipped: { reason: string; kind: 'old_version' | 'player_type' | 'other'; count: number }[];
  };
  /** The list refresh the inspector waits on next; null when there was nothing to refresh. */
  refresh_operation_id: string | null;
}

/** The async-operation type of the ban itself (scope LIVE-TRACK-{id}). */
export const TRACK_BAN_EVERYWHERE_OPERATION_TYPE = 'track_ban_everywhere';

/** The async-operation type of the list refresh that follows a ban (scope LIVE-TRACK-{id}). */
export const TRACK_BAN_LIST_REFRESH_OPERATION_TYPE = 'track_ban_list_refresh';

export function liveTrackUsagesQueryKey(liveTrackId: number | null) {
  return ['liveTrackUsages', liveTrackId] as const;
}

/**
 * Queue the ban of the track in every zone it is on. The server decides the zones and answers
 * 202 with the async operation to follow; a second press while it runs gets the same one back.
 */
export function banLiveTrackEverywhere(liveTrackId: number): Promise<string> {
  return http
    .post<{ data: { message: string; meta: { async_operation_id: string } } }>(
      `/api/live_track/${liveTrackId}/ban-everywhere`,
    )
    .then((response) => response.data.data.meta.async_operation_id);
}

/**
 * Full usage list for a track (every bucket / custom playlist that includes it, with the
 * owning client/location/zone) from GET /api/live_track/{id}/usages. Data is rebuilt by
 * the nightly playlist refresh, so it reflects last night's run.
 */
export function useLiveTrackUsages(liveTrackId: MaybeRefOrGetter<number | null | undefined>) {
  return useQuery({
    queryKey: computed(() => liveTrackUsagesQueryKey(toValue(liveTrackId) ?? null)),
    enabled: computed(() => {
      const trackId = toValue(liveTrackId);
      return typeof trackId === 'number' && Number.isFinite(trackId);
    }),
    queryFn: () =>
      http
        .get<LiveTrackUsagesResponse>(`/api/live_track/${toValue(liveTrackId)}/usages`)
        .then((response) => response.data),
    // The table changes nightly, or after a ban, which refetches it. Reopening the section
    // within half a minute does not need to ask again.
    staleTime: 30_000,
  });
}

export interface PlaylistUsageTracksResponse {
  list: {
    source_type: 'bucket' | 'custom_playlist';
    source_id: number;
    name: string;
    votes_context?: TrackVotesContext | null;
  };
  data: PlaylistTrackRow[];
  meta: {
    /** Exact count on the first page only; later pages skip the count and send null. */
    total: number | null;
    per_page: number;
    /** Opaque forward-only token for the next page; null once the list is exhausted. */
    next_cursor: string | null;
  };
}

/**
 * Resolved tracks for one usage row (bucket or custom playlist), shaped for TrackCard
 * and cursor-paged by the backend in batches of 500. Infinite query: call fetchNextPage
 * to load the next batch (buckets can resolve thousands of tracks). Each page hands
 * back meta.next_cursor, which is sent as ?cursor= for the next one.
 */
export function usePlaylistUsageTracks(
  sourceType: MaybeRefOrGetter<'bucket' | 'custom_playlist'>,
  sourceId: MaybeRefOrGetter<number | null | undefined>,
) {
  return useInfiniteQuery({
    queryKey: computed(() => ['playlistUsageTracks', toValue(sourceType), toValue(sourceId) ?? null]),
    enabled: computed(() => {
      const id = toValue(sourceId);
      return typeof id === 'number' && Number.isFinite(id);
    }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: PlaylistUsageTracksResponse) =>
      lastPage.meta.next_cursor ?? undefined,
    queryFn: ({ pageParam }) =>
      http
        .get<PlaylistUsageTracksResponse>(
          `/api/live_track_usages/${toValue(sourceType)}/${toValue(sourceId)}/tracks`,
          { params: pageParam ? { cursor: pageParam } : {} }
        )
        .then((response) => response.data),
  });
}
